/**
 * Validador de contraseñas robusto para frontend
 * Replica la lógica del backend para validación en tiempo real
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  checks: {
    minLength: boolean;
    hasUpperCase: boolean;
    hasLowerCase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
    notCommon: boolean;
    noRepeatedChars: boolean;
    noSequences: boolean;
  };
}

// Lista de contraseñas comunes
const COMMON_PASSWORDS = [
  'password', '123456', '123456789', '12345678', '12345', '1234567',
  '1234567890', 'qwerty', 'abc123', 'monkey', 'letmein', 'trustno1',
  'dragon', 'baseball', 'iloveyou', 'master', 'sunshine', 'ashley',
  'bailey', 'passw0rd', 'shadow', '123123', '654321', 'superman',
  'qazwsx', 'michael', 'football', 'welcome', 'jesus', 'ninja',
  'mustang', 'password1', 'admin', '1234', 'qwerty123'
];

// Secuencias comunes de teclado
const KEYBOARD_SEQUENCES = ['12345', 'abcde', 'qwerty', 'asdfg', 'zxcvb'];

export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];
  const checks = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>\[\]\\/_+=\-~`]/.test(password),
    notCommon: !COMMON_PASSWORDS.includes(password.toLowerCase()),
    noRepeatedChars: !/(.)\1{4,}/.test(password),
    noSequences: !hasKeyboardSequence(password.toLowerCase()),
  };

  if (!checks.minLength) {
    errors.push('La contraseña debe tener al menos 8 caracteres');
  }
  if (!checks.hasUpperCase) {
    errors.push('Debe contener al menos una letra mayúscula');
  }
  if (!checks.hasLowerCase) {
    errors.push('Debe contener al menos una letra minúscula');
  }
  if (!checks.hasNumber) {
    errors.push('Debe contener al menos un número');
  }
  if (!checks.hasSpecialChar) {
    errors.push('Debe contener al menos un carácter especial (!@#$%^&*...)');
  }
  if (!checks.notCommon) {
    errors.push('La contraseña es demasiado común');
  }
  if (!checks.noRepeatedChars) {
    errors.push('No puede contener el mismo carácter repetido más de 4 veces');
  }
  if (!checks.noSequences) {
    errors.push('No puede contener secuencias comunes de teclado');
  }

  return {
    isValid: errors.length === 0,
    errors,
    checks,
  };
}

function hasKeyboardSequence(password: string): boolean {
  for (const seq of KEYBOARD_SEQUENCES) {
    if (password.includes(seq) || password.includes(seq.split('').reverse().join(''))) {
      return true;
    }
  }
  return false;
}

/**
 * Obtiene mensajes de error amigables para mostrar al usuario
 */
export function getPasswordRequirements(): string[] {
  return [
    'Al menos 8 caracteres',
    'Al menos una letra mayúscula',
    'Al menos una letra minúscula',
    'Al menos un número',
    'Al menos un carácter especial (!@#$%^&*...)',
  ];
}

